import { useContext, useEffect, useState } from "react";
import styles from "./styles/PosShoppingCart.module.css";
import DataContext from "../../../context/DataContext";
import { useTranslation } from "react-i18next";
import POSDataContext from "../../../context/POSDataContext";
import Button from "../../../components/ui/button/Button";
import AuthContext from "../../../context/AuthContext";
import { axiosSW } from "../../../api/axiosDefaults";
import PosCartTicket from "../../../components/ui/pos_cart_ticket/PosCartTicket";
import Spinner from "../../../components/ui/spinner/Spinner";
import PosCartService from "../../../components/ui/pos_cart_service/PosCartService";
import PosCartPayment from "../../../components/ui/pos_cart_payment/PosCartPayment";
import OutputWindow from "../../../components/ui/output_window/OutputWindow";
import { useNavigate } from "react-router-dom";
import PosCartSettlement from "../../../components/ui/pos_cart_settlement/PosCartSettlement";
import useCurrencySymbol from "../../../hooks/useCurrencySymbol";
import useNumberFormat from "../../../hooks/useNumberFormat";

/**
 * Item panel component
 * The over all process
 * 1- The panel will show no items message and a go back button if there are no items fetched
 * 2- A list of the tickets available will be shown with the sum of the mount and the tax
 * 3- A list of the services available will be shown with the sum of the mount and the tax
 * 4- The add payment button will apear instead of the go back button, the button willbe at the bottom if there are no payments
 *    and once apayment is there the button will appear on top of the payment panel
 * 5- Once there is a payment saved, the payment panel will appear to the right of the main panel
 * 6- A submit button will apper at the bottom of the panel
 * 7- The add payment button is active till the amount left to pay is 0
 * 8- The submit button is active when the amount left to pay is 0
 *
 * IMPORTANT!!! The styles are changing also depanding on the validation
 */
const PosItemsPanel = ({
    shoppingCartItems, // {Array} - The array of items in the shopping cart
    fetchShoppingCart, // {Function} - Function to fetch shopping cart data
    setShowPayment, // {Function} - Function to set the visibility of the payment modal
    calculateAmountToPay, // {Function} - Function to calculate the amount left to pay
    getOverAllTotal, // {Function} - Function to get the overall total amount
    isPaying, // {Boolean} - Indicates if the payment process is ongoing
    setIsPaying, // {Function} - Function to set the payment process state
}) => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const {
        connection, // {Object} - The current client's settings
        homePath, // {String} - The path to the home page
        currentCustom, // {Object} - The current client's customizations
    } = useContext(DataContext);
    const {
        user, // {Object} - The authenticated user data object
    } = useContext(AuthContext);
    const {
        isLoadingPos, // {Boolean} - Indicates if the data is loading in the POS module
        setIsLoadingPos, // {Function} - Function to set the loading state in the  POS module
        setResultPos, // {Function} - Function to set the result data for POS module
        payments, // {Array} - The array of payments entered
        setPayments, // {Function} - Function to save the payments
        handleErrorPos, // {Function} - Function to handle errors in POS module
        count, // {Number} - The count of items in the shopping cart
        setCount, // {Function} - Function to set the count of items in the shopping cart
        userLogout,
    } = useContext(POSDataContext);

    const [filteredCases, setFilteredCases] = useState([]);
    // const [isCaseDeleting, setIsCaseDeleting] = useState(false);

    const currency = useCurrencySymbol(true);
    const formatNum = useNumberFormat();

    // useEffect(() => {
    //     if (!shoppingCartItems?.length) {
    //         setPayments([]);
    //     }
    // }, [shoppingCartItems]);

    useEffect(() => {
        if (!shoppingCartItems || !shoppingCartItems?.length) return;
        const groupedCases = shoppingCartItems.filter(
            (item) => item.productType === "case",
        );
        setFilteredCases(groupedCases);
    }, [shoppingCartItems]);

    /**
     * Delete the payment from the local storage
     * The payments are saved localy and when the amount is correct they can be submited
     * @param {Integer} id - The id of the payment
     */
    const handleDeletePayment = (id) => {
        setPayments((prevPayments) =>
            prevPayments.filter((payment) => payment.id !== id),
        );
    };

    /**
     * Delete shooping cart item
     * @param {Integer} id - The id of the cart item
     */
    const handleDelete = async (id) => {
        const payLoad = {
            uniqueIdentifier: connection?.uniqueIdentifier,
            cartItemID: id,
        };

        try {
            setIsLoadingPos(true);

            const { data } = await axiosSW.post(
                "ShoppingCart/pos/DeleteShoppingCartItem",
                payLoad,
            );

            if (data?.statusOk) {
                setIsLoadingPos(false);
                if (shoppingCartItems?.length === 1) {
                    setPayments([]);
                }
                fetchShoppingCart();
            }
        } catch (error) {
            if (error?.request?.status === 404) {
                setIsLoadingPos(false);
                userLogout();
            } else {
                setIsLoadingPos(false);
                handleErrorPos(error);
            }
        }
    };
    // calculate each category sum

    /**
     * Calculate the sum of the amounts and tax for services and violations (tickets)
     * The function can be used on both tickets and services
     * @param {String} type - The targeted product (can be violation or service)
     * @param {String} sumType - The amount type to sum (can be amount or tax)
     * @returns {Float} - tha sum of the amounts
     */
    const getSum = (type, sumType) => {
        let total = 0;

        shoppingCartItems &&
            shoppingCartItems.length &&
            shoppingCartItems
                .filter((item) => item?.productType === type) // check the typeto calculate
                .forEach((item) => {
                    if (sumType === "amount") {
                        total += item?.subTotal ? item?.subTotal : 0;
                    } else if (sumType === "tax") {
                        total += item?.taxTotal ? item?.taxTotal : 0;
                    } else {
                        total += 0;
                    }
                });

        return total;
    };

    /**
     * Submit the payments data to the server
     * @param {Object} payLoad - The payload object
     */
    const submitData = async (payLoad) => {
        // -------------------------------------------------------- Test
        // console.log("payLoad: ", payLoad);
        // return;
        // -------------------------------------------------------- Test

        setIsLoadingPos(true);
        setIsPaying(true);
        try {
            const { data } = await axiosSW.post(
                "Payment/pos/ProcessPayment",
                payLoad,
            );

            if (data?.statusOk) {
                setShowPayment(false);
                setPayments([]);
                setResultPos({ transactionNumber: data?.referenceNo });
                setCount(0);
                navigate(`${homePath}/result`);
                setIsLoadingPos(false);
                setIsPaying(false);
            }
        } catch (error) {
            if (error?.request?.status === 404) {
                setIsLoadingPos(false);
                setIsPaying(false);
                userLogout();
            } else {
                setIsLoadingPos(false);
                setIsPaying(false);

                handleErrorPos(error);
            }
        }
    };

    /**
     * Submit handle
     * create a payload and use for submiting data
     * @param {Event} event - The event object
     */
    const handleSubmit = async (event) => {
        event.preventDefault();

        // Collect the payments data from local storage
        const paymentDetails =
            payments &&
            payments.length &&
            payments.map((payment) => {
                const { id, ...details } = payment;
                return details;
            });

        const chargeIDs =
            shoppingCartItems &&
            shoppingCartItems?.length &&
            shoppingCartItems?.reduce((acc, item) => {
                if (Array.isArray(item.installmentItems)) {
                    acc.push(...item.installmentItems.map((i) => i.chargeID));
                }
                return acc;
            }, []);
        const payLoad = {
            uniqueIdentifier: connection?.uniqueIdentifier,
            cashierSessionID: user?.cashierSessionID,
            cartID: user?.cartID,
            paymentMethods: paymentDetails,
            chargeIDs: chargeIDs,
        };

        setIsLoadingPos(true);
        submitData(payLoad);
    };

    /**
     * The overall total window content
     * @returns {JSX.Element} - The content of the overall total window.
     */
    const getBalanceWindowContnent = () => {
        return (
            <h3
                className={`${styles.payment_output_content_container} flex-container `}
            >
                <span className={`${styles.payment_output_label} `}>
                    {`${t("overall sum")} :`}
                </span>
                <span className={`${styles.payment_output_amount}`}>
                    {currency} {formatNum(getOverAllTotal())}
                </span>
            </h3>
        );
    };

    /**
     * The remaining amount to pay window content
     * @returns {JSX.Element} - The content of the remaining amount to pay window.
     */
    const getRemainBalanceWindowContnent = () => {
        return (
            <h3
                className={`${styles.payment_output_content_container} flex-container `}
            >
                <span className={`${styles.payment_output_label}`}>
                    {`${t("amount to pay")} :`}
                </span>
                <span className={`${styles.payment_output_amount}`}>
                    {currency}
                    {formatNum(calculateAmountToPay())}
                </span>
            </h3>
        );
    };

    /**
     * Redirect on step back in window's history
     */
    const goBack = () => {
        navigate(-1);
    };

    return (
        <section className={`${styles.main_side_container} v-flex-container`}>
            <div
                className={`${styles.panels_container} ${
                    count !== 0 && !isPaying && payments && payments.length
                        ? "flex-container"
                        : "v-flex-container"
                }`}
            >
                <section
                    className={`${styles.main_side_tickets_container}  ${
                        !payments || !payments.length
                            ? styles.tickets_container_full_width
                            : ""
                    } ${styles.stretch} v-flex-container`}
                >
                    {isLoadingPos ? (
                        <Spinner />
                    ) : count === 0 ? (
                        <div
                            className={`${styles.cart_message} flex-container`}
                        >
                            <h2>{t("shopping cart is empty")}</h2>
                        </div>
                    ) : (
                        <>
                            {/* Check if there are any tickets */}
                            {shoppingCartItems?.length &&
                            shoppingCartItems.filter(
                                (item) => item?.productType === "violation",
                            ).length ? (
                                //_____________________________________________________________________________________Tickets

                                <>
                                    <h4
                                        className={`${styles.tickets_label} flex-container`}
                                    >
                                        {t(`tickets`)}
                                    </h4>
                                    <div
                                        className={`${styles.tickets_header} flex-container`}
                                    >
                                        <span
                                            className={`${styles.header_entry} ${styles.ticket_lable_number}`}
                                        >
                                            {`${t("ticket")}`}
                                        </span>

                                        <span
                                            className={`${styles.header_entry} ${styles.hide_info} ${styles.ticket_lable_tax}`}
                                        >
                                            {t("tax")}
                                        </span>
                                        <span
                                            className={`${styles.header_entry}  ${styles.ticket_lable_amount}`}
                                        >
                                            {t("amount")}
                                        </span>
                                    </div>
                                    {/* List available tickets */}
                                    {shoppingCartItems
                                        .filter(
                                            (item) =>
                                                item?.productType ===
                                                "violation",
                                        )
                                        .map((item) => (
                                            <PosCartTicket
                                                key={item?.cartItemID}
                                                ticket={item}
                                                tax={item?.taxTotal}
                                                currency={currency}
                                                handleDelete={handleDelete}
                                            />
                                        ))}
                                    <div
                                        className={`${styles.main_sum_container} v-flex-container`}
                                    >
                                        <div
                                            className={`${styles.sum_container} flex-container`}
                                        >
                                            <span
                                                className={`${styles.sum_label}`}
                                            >
                                                {`${t("tax sum")} :`}
                                            </span>
                                            <span
                                                className={`${styles.sum_value} `}
                                            >
                                                {/* the tickets tax sum */}
                                                {`${currency}${formatNum(
                                                    getSum("violation", "tax"),
                                                )}`}
                                            </span>
                                        </div>
                                        <div
                                            className={`${styles.sum_container} flex-container`}
                                        >
                                            <span
                                                className={`${styles.sum_label}`}
                                            >
                                                {`${t("amount sum")} :`}
                                            </span>
                                            <span
                                                className={`${styles.sum_value} `}
                                            >
                                                {/* the tickets amount sum */}
                                                {`${currency}${formatNum(
                                                    getSum(
                                                        "violation",
                                                        "amount",
                                                    ),
                                                )}`}
                                            </span>
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <></>
                            )}

                            {/* Check if there are any settlements */}
                            {filteredCases?.length ? (
                                //_____________________________________________________________________________________settlements

                                <>
                                    <h4
                                        className={`${styles.tickets_label} flex-container`}
                                    >
                                        {t(`settlements`)}
                                    </h4>
                                    <div
                                        className={`${styles.tickets_header} flex-container`}
                                    >
                                        <span
                                            className={`${styles.header_entry} ${styles.ticket_lable_number}`}
                                        >
                                            {`${t("case")}`}
                                        </span>

                                        <span
                                            className={`${styles.header_entry} ${styles.instalment_hide_info} ${styles.ticket_lable_tax}`}
                                        >
                                            {t("tax")}
                                        </span>
                                        <span
                                            className={`${styles.header_entry} ${styles.ticket_lable_amount} `}
                                        >
                                            {t("amount")}
                                        </span>
                                    </div>
                                    {/* List available tickets */}
                                    {filteredCases?.length &&
                                        filteredCases.map((item, idx) => (
                                            <PosCartSettlement
                                                key={`${item?.cartItemID}-${idx}`}
                                                ticket={item}
                                                currency={currency}
                                                handleDelete={handleDelete}
                                            />
                                        ))}
                                    <div
                                        className={`${styles.main_sum_container} v-flex-container`}
                                    >
                                        <div
                                            className={`${styles.sum_container} flex-container`}
                                        >
                                            <span
                                                className={`${styles.sum_label}`}
                                            >
                                                {`${t("tax sum")} :`}
                                            </span>
                                            <span
                                                className={`${styles.sum_value} `}
                                            >
                                                {/* the tickets tax sum */}
                                                {`${currency}${formatNum(
                                                    getSum("case", "tax"),
                                                )}`}
                                            </span>
                                        </div>
                                        <div
                                            className={`${styles.sum_container} flex-container`}
                                        >
                                            <span
                                                className={`${styles.sum_label}`}
                                            >
                                                {`${t("amount sum")} :`}
                                            </span>
                                            <span
                                                className={`${styles.sum_value} `}
                                            >
                                                {/* the tickets amount sum */}
                                                {`${currency}${formatNum(
                                                    getSum("case", "amount"),
                                                )}`}
                                            </span>
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <></>
                            )}

                            {/* Check if there are any services available */}
                            {shoppingCartItems?.length &&
                            shoppingCartItems.filter(
                                (item) => item?.productType === "service",
                            ).length ? (
                                //_____________________________________________________________________________________Services

                                <>
                                    <h4
                                        className={`${styles.tickets_label} flex-container`}
                                    >
                                        {t(`services`)}
                                    </h4>
                                    <div
                                        className={`${styles.services_header} flex-container`}
                                    >
                                        <span
                                            className={`${styles.service_entry} ${styles.service_label_name}`}
                                        >
                                            {t("service")}
                                        </span>
                                        <span
                                            className={`${styles.service_entry} ${styles.service_hide_info} ${styles.service_label_code}`}
                                        >
                                            {t("code")}
                                        </span>

                                        <span
                                            className={`${styles.service_entry} ${styles.service_hide_info_2nd} ${styles.ticket_lable_tax}`}
                                        >
                                            {t("tax")}
                                        </span>
                                        <span
                                            className={`${styles.service_entry} ${styles.ticket_lable_amount}`}
                                        >
                                            {t("amount")}
                                        </span>
                                    </div>
                                    {/* List the available services */}
                                    {shoppingCartItems
                                        .filter(
                                            (item) =>
                                                item?.productType === "service",
                                        )
                                        .map((item) => (
                                            <PosCartService
                                                key={item?.cartItemID}
                                                service={item}
                                                searched={true}
                                                tax={item?.taxTotal}
                                                currency={currency}
                                                handleDelete={handleDelete}
                                            />
                                        ))}
                                    <div
                                        className={`${styles.main_sum_container} v-flex-container`}
                                    >
                                        <div
                                            className={`${styles.sum_container} flex-container`}
                                        >
                                            <span
                                                className={`${styles.sum_label}`}
                                            >
                                                {`${t("tax sum")} :`}
                                            </span>
                                            <span
                                                className={`${styles.sum_value} `}
                                            >
                                                {`${currency}${formatNum(
                                                    getSum("service", "tax"),
                                                )}`}
                                            </span>
                                        </div>
                                        <div
                                            className={`${styles.sum_container} flex-container`}
                                        >
                                            <span
                                                className={`${styles.sum_label}`}
                                            >
                                                {`${t("amount sum")} :`}
                                            </span>
                                            <span
                                                className={`${styles.sum_value} `}
                                            >
                                                {`${currency}${formatNum(
                                                    getSum("service", "amount"),
                                                )}`}
                                            </span>
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <></>
                            )}
                            <div
                                className={`${styles.cart_main_windows_container} flex-container`}
                            >
                                <div
                                    className={`${styles.payment_output_window_container} flex-container`}
                                >
                                    {/* The overall amount window */}
                                    <OutputWindow
                                        color={currentCustom?.color}
                                        content={getBalanceWindowContnent()}
                                        padding={"1em"}
                                        fullWidth={true}
                                    />
                                </div>
                                <div
                                    className={`${styles.payment_output_window_container} flex-container`}
                                >
                                    {/* The amount left to pay window */}
                                    <OutputWindow
                                        color={
                                            // used  Math.abs(calculateAmountToPay()) < 1e-10 to compinsate for js float error
                                            Math.abs(calculateAmountToPay()) <
                                            1e-10
                                                ? "#03C812"
                                                : "#b60000"
                                        }
                                        content={getRemainBalanceWindowContnent()}
                                        padding={"1em"}
                                        fullWidth={true}
                                    />
                                </div>
                            </div>
                        </>
                    )}
                </section>

                <section
                    className={`${
                        count !== 0 && payments && payments.length
                            ? styles.main_payments_container
                            : ""
                    } ${styles.stretch}  v-flex-container`}
                >
                    <div
                        className={`${styles.payment_button_container} flex-container`}
                    >
                        {shoppingCartItems?.length ? (
                            <Button
                                body={t("add payment")}
                                type={"button"}
                                size={"adapt"}
                                color={"green"}
                                onClickFuction={() => setShowPayment(true)}
                                disabled={
                                    count === 0 ||
                                    !(calculateAmountToPay() > 0) ||
                                    !calculateAmountToPay() === 0
                                }
                                disableHTML={
                                    count === 0 ||
                                    !(calculateAmountToPay() > 0) ||
                                    !calculateAmountToPay() === 0
                                }
                            />
                        ) : (
                            <Button
                                body={t("go back")}
                                type={"button"}
                                size={"adapt"}
                                onClickFuction={goBack}
                            />
                        )}
                    </div>
                    {/* The payments */}
                    {/* Check if there are any payments */}
                    {count !== 0 && !isPaying && payments && payments.length ? (
                        //_____________________________________________________________________________________Payments

                        <div
                            className={`${styles.main_side_payments_container} v-flex-container`}
                        >
                            {count === 0 ? (
                                <div
                                    className={`${styles.cart_message} flex-container`}
                                >
                                    <h2>{t("shopping cart is empty")}</h2>
                                </div>
                            ) : (
                                <>
                                    {payments && payments.length ? (
                                        <>
                                            <h4
                                                className={`${styles.tickets_label} flex-container`}
                                            >
                                                {t(`payments`)}
                                            </h4>
                                            <div
                                                className={`${styles.tickets_header} flex-container`}
                                            >
                                                <span
                                                    className={`${styles.header_entry} ${styles.payments_header_type_label}`}
                                                >
                                                    {t(`type`)}
                                                </span>
                                                <span
                                                    className={`${styles.header_entry} ${styles.payments_header_amount_label}`}
                                                >
                                                    {t(`amount`)}
                                                </span>
                                            </div>
                                            {/* List the payments available */}
                                            {payments.map((payment, idx) => (
                                                <PosCartPayment
                                                    key={`${payments?.type}${idx}`}
                                                    payment={payment}
                                                    currency={currency}
                                                    handleDelete={
                                                        handleDeletePayment
                                                    }
                                                />
                                            ))}
                                        </>
                                    ) : (
                                        <></>
                                    )}
                                </>
                            )}
                        </div>
                    ) : (
                        <></>
                    )}
                </section>
            </div>
            {/* Show the submit button if there is a payment */}
            {count !== 0 && payments && payments.length ? (
                <div
                    className={`${styles.cart_button_container} flex-container`}
                >
                    <Button
                        body={t("submit")}
                        type={"button"}
                        size={"adapt"}
                        onClickFuction={handleSubmit}
                        disabled={Math.abs(calculateAmountToPay()) > 0}
                        disableHTML={Math.abs(calculateAmountToPay()) > 0}
                    />
                </div>
            ) : (
                <></>
            )}
        </section>
    );
};

export default PosItemsPanel;
